import React, { useState, useEffect } from "react";

import {
  useTable,
  useRowSelect,
  useSortBy,
  usePagination,
  useFilters,
} from "react-table";

import {
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Icon,
} from "@chakra-ui/react";

import { FaSortUp, FaSortDown, FaSort } from "react-icons/fa";

import PaginationRadioGroupTable from "../PaginationRadioGroupTable";
import { RadioButtonField } from "../RadioButtonField";
import { normalizeString } from "../../utils/masks";

const IndeterminateRadio = React.forwardRef(
  ({ indeterminate, isDisabled, index, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
      <input
        type="radio"
        id={`radio-selecione-${index}`}
        ref={resolvedRef}
        {...rest}
        style={{
          ...rest.style,
          width: "20px",
          height: "20px",
        }}
        disabled={isDisabled && isDisabled}
      />
    );
  }
);

const RadioGroupTable = (props) => {
  const { data, columns, selected, edit, setSelected, hasOrdernation } = props;

  const [nItens] = useState(10);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    pageOptions,
    page,
    pageCount,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    toggleAllRowsSelected,
    toggleRowSelected,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      autoResetSelectedRows: false,
      initialState: {
        filters: [],
        sortBy: hasOrdernation
          ? [
              {
                id: "nome",
                desc: false,
              },
            ]
          : [],
        pageIndex: 0,
        pageSize: 10,
      },
    },
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        {
          id: "selection",
          Header: "Selecione",
          Cell: ({ row }) => (
            <Flex justifyContent="center">
              {edit ? (
                <RadioButtonField
                  fieldName={"idOrgao"}
                  value={`${
                    props.promotoria ? row.original.codigo : row.original.id
                  }`}
                  label=""
                  edit={edit}
                />
              ) : (
                <IndeterminateRadio
                  index={row.index}
                  {...row.getToggleRowSelectedProps()}
                  onClick={() => {
                    toggleAllRowsSelected(false);
                    toggleRowSelected(row.id, true);
                    setSelected(row.original);
                  }}
                  isDisabled={
                    props.filiacao &&
                    (selected?.some((item) => item.id === row?.original?.id) ||
                      selected?.some(
                        (item) =>
                          normalizeString(item?.nome) ===
                          normalizeString(row?.original?.nome)
                      ))
                  }
                />
              )}
            </Flex>
          ),
        },
        ...columns,
      ]);
    }
  );

  useEffect(() => {
    if (selected && !edit) {
      const tempRow = rows.find((row) => row.original.id === selected.id);
      if (tempRow) {
        toggleAllRowsSelected(false);
        toggleRowSelected(tempRow.id, true);
      }
    }
  }, [edit, rows, selected, toggleAllRowsSelected, toggleRowSelected]);

  useEffect(() => {
    if (!edit) {
      toggleAllRowsSelected(false);
    }
  }, [data, edit, toggleAllRowsSelected]);

  return (
    <Flex flexDir="column" w="100%">
      <Flex flexDir="column" width="100%" mt={5} h="100%">
        <Flex w={"100%"} flexDir={"column"}>
          <Flex flexDir={"column"}>
            <Flex overflowX="auto" pb={5}>
              <Table
                {...getTableProps}
                backgroundColor="white"
                overflowX="auto"
              >
                <Thead backgroundColor="#ECEFF1">
                  {headerGroups.map((headerGroup, index) => (
                    <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                      {headerGroup.headers.map((column, index) => (
                        <Th
                          {...column.getHeaderProps(
                            hasOrdernation &&
                              column.getSortByToggleProps({ title: undefined })
                          )}
                          w={
                            column.Header === "Nome"
                              ? "60%"
                              : column.Header === "Município" && "30%"
                          }
                          key={index}
                          p="0px"
                          width={column.Header === "Filho(a)" && "290px"}
                        >
                          <Flex
                            flexDir="row"
                            alignContent="center"
                            px="24px"
                            py="12px"
                          >
                            <Text as="p">{column.render("Header")}</Text>

                            {column.Header !== "Selecione" &&
                            column.Header !== "Filho(a)" ? (
                              <Flex pl={1} alignItems="center">
                                {column.isSorted ? (
                                  column.isSortedDesc ? (
                                    <Icon as={FaSortDown} boxSize="14px" />
                                  ) : (
                                    <Icon as={FaSortUp} boxSize="14px" />
                                  )
                                ) : (
                                  <Icon as={FaSort} boxSize="14px" />
                                )}
                              </Flex>
                            ) : (
                              ""
                            )}
                          </Flex>
                        </Th>
                      ))}
                    </Tr>
                  ))}
                </Thead>
                <Tbody {...getTableBodyProps}>
                  {page.map((row, i) => {
                    prepareRow(row);
                    return (
                      <>
                        {i !== 0 && props.filiacao && (
                          <Tr display="flex" mt={3} />
                        )}

                        <Tr
                          border={props.filiacao && "3px solid #DDEBEA"}
                          {...row.getRowProps()}
                          key={i}
                          whiteSpace="nowrap"
                        >
                          {row.cells.map((cell, index) => {
                            return (
                              <Td
                                key={index}
                                {...cell.getCellProps()}
                                py="6px"
                                fontWeight={500}
                                fontSize="14px"
                                lineHeight="19.6px"
                              >
                                {cell.render("Cell")}
                              </Td>
                            );
                          })}
                        </Tr>
                      </>
                    );
                  })}
                </Tbody>
              </Table>
            </Flex>

            <PaginationRadioGroupTable
              nItens={nItens}
              selecionarTotalItens={setPageSize}
              irParaPagina={gotoPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
              rows={rows}
              gotoPage={gotoPage}
              canPreviousPage={canPreviousPage}
              previousPage={previousPage}
              pageOptions={pageOptions}
              pageIndex={pageIndex}
              canNextPage={canNextPage}
              nextPage={nextPage}
              pageCount={pageCount}
              edit={edit}
            />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default RadioGroupTable;
